/*
 * ReferralCard — Stage 11 Referral Engine surface.
 * Reads /api/account/referral and lets the user copy, share, and watch their
 * bonus days rack up. Kept intentionally calm — this is not a shouty SaaS
 * widget; it lives quietly inside the profile alongside other account cards.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Copy, ShareNetwork, Check, Gift } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios, { API } from '../../services/api';
import { track } from '../../services/activation';

export const ReferralCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/account/referral`);
        if (!active) return;
        setData(data);
        try { track('referral_code_viewed', { code: data.code, surface: 'profile' }); } catch { /* noop */ }
      } catch {
        // Non-blocking: the card simply hides on error.
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleCopy = async () => {
    if (!data?.share_url) return;
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
      toast.success('Referral link copied');
      try { track('referral_link_copied', { code: data.code }); } catch { /* noop */ }
    } catch {
      toast.error('Could not copy — long-press the link to copy manually');
    }
  };

  const handleShare = async () => {
    if (!data?.share_url) return;
    const shareData = {
      title: 'Kindra — care between appointments',
      text: 'Use my link to start a free 30-day trial of Kindra — and we both get a bonus month:',
      url: data.share_url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        try { track('referral_link_shared', { code: data.code, channel: 'native' }); } catch { /* noop */ }
      } else {
        await handleCopy();
      }
    } catch (e) {
      // User-cancel or share aborted — ignore silently.
      if (e?.name !== 'AbortError') {
        await handleCopy();
      }
    }
  };

  if (loading || !data?.code) return null;

  const invited = Number(data.invited || 0);
  const earnedDays = Number(data.earned_days || 0);
  const bonusPerInvite = Number(data.bonus_days_per_invite || 30);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="rounded-2xl border border-[#ECE8E1] bg-gradient-to-br from-[#FAF7EF] to-white p-5 shadow-[0_1px_2px_rgba(14,28,46,0.04)]"
      data-testid="referral-card"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-xl bg-[#235347]/10 flex items-center justify-center">
          <Gift size={20} className="text-[#235347]" weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 font-medium">
            Invite a friend
          </p>
          <h3
            className="text-[20px] text-slate-900 leading-snug mt-0.5"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Give a friend <em className="italic">30 days free.</em> Get the same back.
          </h3>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            When someone uses your link to sign up, you both get {bonusPerInvite} bonus days added to your trial.
          </p>
        </div>
      </div>

      {/* Link row */}
      <div className="mt-4 flex items-center gap-2 rounded-xl bg-white border border-[#E6DEC9] px-3 py-2.5">
        <span
          className="flex-1 min-w-0 truncate text-[13px] font-mono text-slate-700"
          data-testid="referral-share-url"
          title={data.share_url}
        >
          {data.share_url}
        </span>
        <button
          onClick={handleCopy}
          className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-[#235347] hover:text-[#1A3E35] transition-colors px-2 py-1 rounded-md hover:bg-[#235347]/5"
          data-testid="referral-copy-btn"
        >
          {copied ? <Check size={14} weight="bold" /> : <Copy size={14} weight="duotone" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {/* Stats row + share */}
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-5">
          <div>
            <div
              className="text-[22px] text-slate-900 leading-none"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="referral-invited-count"
            >
              {invited}
            </div>
            <p className="text-[10px] tracking-wide uppercase text-slate-400 mt-1">
              Invited
            </p>
          </div>
          <div>
            <div
              className="text-[22px] text-slate-900 leading-none"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="referral-earned-days"
            >
              +{earnedDays}
            </div>
            <p className="text-[10px] tracking-wide uppercase text-slate-400 mt-1">
              Days earned
            </p>
          </div>
          <div>
            <div
              className="text-[22px] text-[#235347] leading-none font-mono tracking-wide"
              data-testid="referral-code"
            >
              {data.code}
            </div>
            <p className="text-[10px] tracking-wide uppercase text-slate-400 mt-1">
              Your code
            </p>
          </div>
        </div>
        <button
          onClick={handleShare}
          className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#235347] hover:bg-[#1A3E35] transition-colors px-3 py-2 rounded-full"
          data-testid="referral-share-btn"
        >
          <ShareNetwork size={14} weight="bold" />
          Share
        </button>
      </div>
    </motion.div>
  );
};

export default ReferralCard;
